/**
 * Pressing indicator + sparkline — React migration Pass 12, port of the
 * template's pressing rail block (app/templates/game_match_summary/index.html
 * ~4988-5206): renderPressingSparkline, updateSparklinePosition,
 * getPressingIndexForFrame, updatePressingIndicatorForFrame,
 * updatePressingIndicator, frameToMatchTime.
 *
 * The indicator DOM (ids pressing-indicator / pressing-ring /
 * pressing-sparkline / sparkline-dot / sparkline-start-time /
 * sparkline-end-time) renders hidden in FilterRail; this module drives it
 * imperatively exactly as the template did — shown when a pressing moment is
 * selected, live-updated from the canvas playback loop, hidden when playback
 * passes the moment's end or the press filters change.
 *
 * Playback state lived on the template's canvasState (pressingPlayback);
 * here it is module state, reset on page unmount via resetPressingIndicator.
 */
import { ScoreRing } from '../../lib/viz/scoreRing'
import type { Moment, PressingIndexPoint } from './types'

// Template COLORS.slate — the sparkline area gradient color.
const SLATE = '#6b7280'

/** The item whose frame is nearest to `frame`, or null for an empty list.
 *
 *  Written three times as `items.reduce(fn, items[0])`, whose seed is a
 *  possibly-missing lookup. Taking the head off first makes the empty case
 *  explicit and the fold over the tail total. */
function nearestByFrame<T extends { frame: number }>(
  items: readonly T[],
  frame: number,
): T | null {
  const [first, ...rest] = items
  if (first === undefined) return null
  return rest.reduce(
    (best, d) => (Math.abs(d.frame - frame) < Math.abs(best.frame - frame) ? d : best),
    first,
  )
}

interface SparklinePoint {
  x: number
  y: number
  frame: number
  value: number
}

// Pressing sparkline state (cached for real-time updates) — template
// pressingSparklineState.
let pressingSparklineState: {
  points: SparklinePoint[]
  startFrame: number
  endFrame: number
  width: number
  height: number
  paddingX: number
  paddingY: number
} = { points: [], startFrame: 0, endFrame: 0, width: 320, height: 64, paddingX: 8, paddingY: 8 }

// Template canvasState.pressingPlayback.
let pressingPlayback: { startFrame: number; endFrame: number; active: boolean } | null = null

// Template state.pressingIndexData (the module's copy; the timeline keeps its
// own React state) — set by GameMatchSummary once /api/pressing-index loads.
let pressingIndexData: PressingIndexPoint[] | null = null

export function setPressingIndexData(data: PressingIndexPoint[] | null): void {
  pressingIndexData = data
}

/** Page unmount: drop playback + data so a remount starts clean. */
export function resetPressingIndicator(): void {
  pressingPlayback = null
  pressingIndexData = null
  pressingSparklineState = {
    points: [], startFrame: 0, endFrame: 0, width: 320, height: 64, paddingX: 8, paddingY: 8,
  }
}

// Convert frame number to M:SS format — template frameToMatchTime.
function frameToMatchTime(frame: number, fps: number): string {
  const totalSeconds = Math.floor(frame / fps)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

// Render pressing sparkline for a moment's frame range.
function renderPressingSparkline(startFrame: number, endFrame: number, currentFrame: number): void {
  const svg = document.getElementById('pressing-sparkline')
  if (!svg || !pressingIndexData || pressingIndexData.length === 0) {
    if (svg) svg.innerHTML = ''
    return
  }

  // Filter data to moment's frame range
  const frameData = pressingIndexData.filter((d) => d.frame >= startFrame && d.frame <= endFrame)

  if (frameData.length < 2) {
    svg.innerHTML = ''
    return
  }

  // SVG dimensions (7:1 aspect ratio, compact for header)
  const width = 280
  const height = 40
  const paddingX = 4
  const paddingY = 4

  // Map frames to x positions
  const minFrame = startFrame
  const maxFrame = endFrame
  const frameRange = maxFrame - minFrame || 1

  // Generate path points
  const points: SparklinePoint[] = frameData.map((d) => {
    const x = paddingX + ((d.frame - minFrame) / frameRange) * (width - 2 * paddingX)
    const y = paddingY + (1 - d.pressing_index) * (height - 2 * paddingY) // Invert Y
    return { x, y, frame: d.frame, value: d.pressing_index }
  })

  // Cache for real-time updates
  pressingSparklineState = { points, startFrame, endFrame, width, height, paddingX, paddingY }

  // Build smooth bezier curve path (Robinhood/Whoop style)
  const firstPoint = points[0]
  const lastPoint = points[points.length - 1]
  if (!firstPoint || !lastPoint) return
  let linePath = `M ${firstPoint.x},${firstPoint.y}`
  for (let i = 0; i + 1 < points.length; i++) {
    const current = points[i]
    const next = points[i + 1]
    if (!current || !next) continue
    // Quadratic bezier for smooth curves
    const controlX = (current.x + next.x) / 2
    linePath += ` Q ${controlX},${current.y} ${next.x},${next.y}`
  }

  // Build area path (closed at bottom)
  let areaPath = linePath
  areaPath += ` L ${lastPoint.x},${height - paddingY}`
  areaPath += ` L ${firstPoint.x},${height - paddingY} Z`

  // Calculate current position indicator
  const currentX = paddingX + ((currentFrame - minFrame) / frameRange) * (width - 2 * paddingX)
  const currentPoint = nearestByFrame(points, currentFrame) ?? firstPoint

  svg.innerHTML = `
        <defs>
            <linearGradient id="sparkline-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stop-color="${SLATE}" stop-opacity="0.25" />
                <stop offset="100%" stop-color="${SLATE}" stop-opacity="0.05" />
            </linearGradient>
        </defs>
        <path class="pressing-sparkline__area" d="${areaPath}" />
        <path class="pressing-sparkline__line" d="${linePath}" />
        <line class="pressing-sparkline__peak-line" id="sparkline-position-line" x1="${currentX}" y1="${paddingY}" x2="${currentX}" y2="${height - paddingY}" />
    `

  // Position the HTML dot element (doesn't get distorted by SVG scaling)
  const dot = document.getElementById('sparkline-dot')
  if (dot) {
    const xPercent = (currentX / width) * 100
    const yPercent = (currentPoint.y / height) * 100
    dot.style.left = `${xPercent}%`
    dot.style.top = `${yPercent}%`
  }
}

// Update sparkline position indicator (called during playback).
function updateSparklinePosition(currentFrame: number): void {
  const { points, startFrame, endFrame, width, height, paddingX } = pressingSparklineState
  if (!points || points.length === 0) return

  const frameRange = endFrame - startFrame || 1
  const currentX = paddingX + ((currentFrame - startFrame) / frameRange) * (width - 2 * paddingX)

  // Find closest point for Y position
  const currentPoint = nearestByFrame(points, currentFrame)
  if (!currentPoint) return

  // Update position line
  const line = document.getElementById('sparkline-position-line')
  if (line) {
    line.setAttribute('x1', String(currentX))
    line.setAttribute('x2', String(currentX))
  }

  // Update position dot (HTML element, not SVG)
  const dot = document.getElementById('sparkline-dot')
  if (dot) {
    const xPercent = (currentX / width) * 100
    const yPercent = (currentPoint.y / height) * 100
    dot.style.left = `${xPercent}%`
    dot.style.top = `${yPercent}%`
  }
}

// Get pressing index for a specific frame (closest point within 1 second).
function getPressingIndexForFrame(frame: number): number | null {
  if (!pressingIndexData || pressingIndexData.length === 0) return null

  // Find closest data point
  const closest = nearestByFrame(pressingIndexData, frame)
  if (!closest) return null

  // Only return if within 30 frames (1 second)
  if (Math.abs(closest.frame - frame) <= 30) {
    return closest.pressing_index
  }
  return null
}

/** The ring's first value for a press: the pressing index at its start
 *  frame when there is one (0 included: a per-frame reading), else the
 *  moment's score when it is above 0 (a served 0 is a missing score),
 *  else null (the empty ring). The ring shows the pressing index only: a
 *  legacy game's press score is that index's peak, but a press marked
 *  score_scale press_score is on the pressing model's scale, so it gets
 *  the empty ring. */
export function initialPressingScore(
  frameIndex: number | null,
  momentScore: number | null | undefined,
  scoreScale?: string | null,
): number | null {
  if (typeof frameIndex === 'number') return frameIndex
  if (scoreScale === 'press_score') return null
  if (typeof momentScore === 'number' && momentScore > 0) return momentScore
  return null
}

/** Called from the canvas playback loop (template animation loop). */
export function updatePressingIndicatorForFrame(currentFrame: number): void {
  const playback = pressingPlayback
  if (!playback || !playback.active) return

  // Check if we've passed the end of the moment
  if (currentFrame > playback.endFrame) {
    // Hide indicator and clear playback state
    const indicator = document.getElementById('pressing-indicator')
    if (indicator) indicator.style.display = 'none'
    pressingPlayback = null
    return
  }

  // Get current pressing index
  const pressingIndex = getPressingIndexForFrame(currentFrame)
  if (pressingIndex !== null) {
    // Update score ring with current value
    ScoreRing.render('pressing-ring', pressingIndex, { variant: 'pressing', size: 'sm' })

    // Update sparkline position
    updateSparklinePosition(currentFrame)
  }
}

/**
 * Show the indicator for a pressing moment (or hide it, moment = null) —
 * template updatePressingIndicator. `pressTypes` was the template's
 * state.currentPressTypes; `fps` its syncState.fps.
 */
export function updatePressingIndicator(
  moment: Moment | null | undefined,
  pressTypes: string[],
  fps: number,
): void {
  const indicator = document.getElementById('pressing-indicator')
  if (!indicator) return

  if (moment && moment.type === 'pressing') {
    const startFrame = moment.frame ?? 0
    const endFrame = moment.end_frame || startFrame + 330 // Default ~11 sec window

    // Set up playback tracking
    pressingPlayback = { startFrame, endFrame, active: true }

    const initialScore = initialPressingScore(getPressingIndexForFrame(startFrame), moment.score,
      moment.score_scale)
    if (initialScore === null) {
      ScoreRing.renderEmpty('pressing-ring', { size: 'sm' })
    } else {
      ScoreRing.render('pressing-ring', Math.min(initialScore, 1), {
        variant: 'pressing',
        size: 'sm',
      })
    }

    // Update indicator label based on selected filter
    const labelEl = indicator.querySelector('.pressing-indicator__label')
    if (labelEl) {
      const onlyType = pressTypes?.length === 1 ? pressTypes[0] : undefined
      if (onlyType !== undefined) {
        // Single filter selected - show specific label
        labelEl.textContent = onlyType
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase())
      } else {
        // Both or none selected - show generic label
        labelEl.textContent = 'Pressing'
      }
    }

    // Update time labels
    const startTimeEl = document.getElementById('sparkline-start-time')
    const endTimeEl = document.getElementById('sparkline-end-time')
    if (startTimeEl) startTimeEl.textContent = frameToMatchTime(startFrame, fps)
    if (endTimeEl) endTimeEl.textContent = frameToMatchTime(endFrame, fps)

    // Render sparkline for moment's frame range
    renderPressingSparkline(startFrame, endFrame, startFrame)

    indicator.style.display = 'flex'
  } else {
    indicator.style.display = 'none'
    pressingPlayback = null
  }
}
